import { oc } from "@orpc/contract";
import { z } from "zod";
import { FileStreamSchema, OffsetPageInputSchema } from "../../schemas/common";

/** The Vendor Account a file was downloaded for, from the downloading step's data inputs. */
export const VendorAccountRefSchema = z.object({
  number: z.string(),
  name: z.string().nullable(),
});

/**
 * Where a Vendor Portal file came from: `download` — the workflow's Browser Use Agent
 * downloaded it; `upload` — a user uploaded it by hand for an account the run missed.
 */
export const VendorPortalFileSourceSchema = z.enum(["download", "upload"]);

/** A file a workflow's Browser Use Agent downloaded from a Vendor Portal, or a user uploaded. */
export const VendorPortalFileSchema = z.object({
  documentId: z.uuid(),
  fileName: z.string(),
  mimeType: z.string().nullable(),
  /** The file name's extension, lowercased without the dot (`xlsx`); null when it has none. */
  fileType: z.string().nullable(),
  fileSizeBytes: z.number().int().nullable(),
  downloadedAt: z.coerce.date(),
  /** The name of the workflow that downloaded the file (or that it was uploaded for). */
  vendor: z.string(),
  /** Null when the downloading step named no Vendor Account. */
  account: VendorAccountRefSchema.nullable(),
  workflowId: z.uuid(),
  /** Null only for an upload attached to no run. */
  workflowRunId: z.uuid().nullable(),
  source: VendorPortalFileSourceSchema,
  /** The name of the user who uploaded the file; null for a download. */
  uploadedBy: z.string().nullable(),
});

export const VendorPortalVendorSchema = z.object({
  name: z.string(),
  fileCount: z.number().int(),
});

/** A file type (a file-name extension, lowercased, without the dot) and its file count. */
export const VendorPortalFileTypeSchema = z.object({
  name: z.string(),
  fileCount: z.number().int(),
});

/** Which files the list shows and a zip download takes. */
const VendorPortalFileFiltersSchema = z.object({
  /** Only files from this vendor (a workflow name). */
  vendor: z.string().min(1).optional(),
  /** Only files for a Vendor Account whose number or name contains this (case-insensitive). */
  account: z.string().trim().min(1).max(200).optional(),
  /**
   * Only files of this type: a file-name extension without the dot (case-insensitive).
   * The list offers only extensions of at most 20 characters.
   */
  fileType: z.string().trim().min(1).max(20).optional(),
  /**
   * Only files downloaded at or after this instant: the start of the user's first day in
   * their own timezone, or the time of day they picked.
   */
  downloadedFrom: z.coerce.date().optional(),
  /**
   * Only files downloaded before this instant: the start of the day after the last one, or
   * the second after the time of day the user picked.
   */
  downloadedBefore: z.coerce.date().optional(),
  /** Only files one workflow run downloaded. */
  workflowRunId: z.uuid().optional(),
});

export const ListVendorPortalFilesInputSchema = OffsetPageInputSchema.extend(
  VendorPortalFileFiltersSchema.shape,
);

/** The most files one delete request may name (a page of the list holds 50). */
export const MAX_VENDOR_PORTAL_FILES_PER_DELETE = 100;

/**
 * The most files one zip download takes (the backend enforces the same cap): one page of
 * the list. Price files run ~20MB each, so this keeps a zip around a gigabyte.
 */
export const MAX_VENDOR_PORTAL_FILES_PER_ARCHIVE = 50;

/** The list rows (1-based, inclusive) a page's zip holds; only names the zip. */
export const VendorPortalArchiveFileRangeSchema = z
  .object({
    first: z.coerce.number().int().min(1),
    last: z.coerce.number().int().min(1),
  })
  .refine((range) => range.last >= range.first, {
    message: "last must not be before first",
  });

export const DownloadVendorPortalFilesArchiveInputSchema =
  VendorPortalFileFiltersSchema.extend({
    /** Only these files, from those the filters match (a selection on the page). */
    documentIds: z
      .array(z.uuid())
      .min(1)
      .max(MAX_VENDOR_PORTAL_FILES_PER_DELETE)
      .optional(),
    /** Which rows of the list these are, for the zip's file name. */
    fileRange: VendorPortalArchiveFileRangeSchema.optional(),
  });

export const CheckVendorPortalFilesArchiveOutputSchema = z.object({
  /**
   * The files the zip will hold, counting those storage no longer has. The page downloads
   * exactly these, so files that arrive after the check cannot push the zip past the cap.
   */
  documentIds: z.array(z.uuid()),
  /** Files storage no longer has; the zip lists them in MISSING-FILES.txt. */
  missingFileCount: z.number().int(),
});

export const DeleteVendorPortalFilesInputSchema = z.object({
  documentIds: z.array(z.uuid()).min(1).max(MAX_VENDOR_PORTAL_FILES_PER_DELETE),
});

export const DeleteVendorPortalFilesOutputSchema = z.object({
  /** Removed from the database and from storage. */
  deleted: z.array(z.uuid()),
  /** Kept because another record still uses the file; `usedBy` names its tables. */
  inUse: z.array(
    z.object({
      documentId: z.uuid(),
      fileName: z.string(),
      usedBy: z.array(z.string()),
    }),
  ),
  /** Kept because the delete failed (e.g. storage was unavailable); safe to retry. */
  failed: z.array(
    z.object({ documentId: z.uuid(), fileName: z.string().nullable() }),
  ),
});

/** File types a user may upload for a Vendor Account: extensions, lowercased, no dot. */
export const VENDOR_PORTAL_UPLOAD_FILE_TYPES = [
  "xlsx",
  "xls",
  "csv",
  "zip",
  "pdf",
] as const;

/** The largest file one upload takes; the same default cap the operator harvest files under. */
export const MAX_VENDOR_PORTAL_UPLOAD_BYTES = 100 * 1024 * 1024;

export const UploadVendorPortalFileInputSchema = z.object({
  /** The vendor: one of the organization's Vendor Portal workflows (see listUploadTargets). */
  workflowId: z.uuid(),
  /** The Vendor Account the file is for. */
  accountNumber: z.string().trim().min(1).max(200),
  /** A run of that workflow to attach the file to; omitted for no run. */
  workflowRunId: z.uuid().optional(),
  file: z.file().min(1).max(MAX_VENDOR_PORTAL_UPLOAD_BYTES),
});

/** A Vendor Portal workflow a file can be uploaded for, with its recent runs. */
export const VendorPortalUploadTargetSchema = z.object({
  workflowId: z.uuid(),
  /** The workflow's name, as the list shows it. */
  vendor: z.string(),
  /**
   * The account-number file-name pattern the workflow's web_browser step last recorded,
   * or null for the default rule (the digits that end the name).
   */
  accountNumberFileNamePattern: z.string().nullable(),
  /** The workflow's most recent runs, newest first. */
  runs: z.array(
    z.object({ workflowRunId: z.uuid(), triggeredAt: z.coerce.date() }),
  ),
});

export const vendorPortalFilesContracts = {
  /**
   * Files the organization's workflows downloaded with the Browser Use Agent, newest
   * first, plus every vendor with its file count for the filter.
   */
  list: oc
    .input(ListVendorPortalFilesInputSchema)
    .output(
      z.object({
        items: z.array(VendorPortalFileSchema),
        /** Files matching the filters, across every page. */
        total: z.number().int(),
        vendors: z.array(VendorPortalVendorSchema),
        /** Every file type with its file count, for the filter. */
        fileTypes: z.array(VendorPortalFileTypeSchema),
      }),
    )
    .route({
      method: "GET",
      summary: "List files downloaded from vendor portals",
      description:
        "Lists a page of files that workflows' browser agents downloaded from vendor portals, newest first, filterable by vendor, account and file type, with the total and per-vendor and per-file-type file counts.",
    }),
  /**
   * Stream every file the filters (and optional ids) match as one zip, at any size.
   * Opened as a browser navigation (startRpcDownloadInFrame), so the browser saves it
   * straight to disk with its own progress.
   */
  downloadArchive: oc
    .input(DownloadVendorPortalFilesArchiveInputSchema)
    .output(FileStreamSchema)
    .route({
      method: "GET",
      summary: "Download vendor portal files as a zip",
      description:
        "Streams every downloaded vendor portal file matching the filters, or the given ids, as one zip archive of any size.",
    }),
  /**
   * Check a zip download without building it: refuses exactly as downloadArchive would
   * (too many files, nothing in storage). The download runs in a hidden frame that cannot
   * show a refusal, so the page asks this first.
   */
  checkArchive: oc
    .input(DownloadVendorPortalFilesArchiveInputSchema)
    .output(CheckVendorPortalFilesArchiveOutputSchema)
    .route({
      method: "GET",
      summary: "Check a vendor portal files zip download",
      description:
        "Dry-runs a zip download with the same filters, reporting whether it would be refused (too many files, nothing in storage) without building the archive.",
    }),
  /**
   * Permanently delete files (the document, its file record and the stored bytes), for
   * downloads from test runs. Files another record still uses are kept; ids that are not
   * this organization's files are ignored.
   */
  delete: oc
    .input(DeleteVendorPortalFilesInputSchema)
    .output(DeleteVendorPortalFilesOutputSchema)
    .route({
      method: "POST",
      summary: "Delete downloaded vendor portal files",
      description:
        "Permanently deletes the documents, file records and stored bytes, for cleaning up test-run downloads. Files still used elsewhere are kept; other organizations' ids are ignored.",
    }),
  /**
   * The Vendor Portal workflows a file can be uploaded for (those that have downloaded
   * files), each with its recent runs and its account-number file-name rule.
   */
  listUploadTargets: oc.output(z.array(VendorPortalUploadTargetSchema)).route({
    method: "GET",
    summary: "List vendor portal workflows a file can be uploaded for",
    description:
      "Lists the organization's vendor portal workflows that have downloaded files, each with its name, recent runs (newest first) and the file-name pattern its account numbers are read with.",
  }),
  /**
   * Upload one file a vendor portal run missed, for one Vendor Account. It is listed with
   * the downloads, marked as uploaded by the current user.
   */
  upload: oc
    .input(UploadVendorPortalFileInputSchema)
    .output(VendorPortalFileSchema)
    .route({
      method: "POST",
      summary: "Upload a vendor portal file for an account",
      description:
        "Uploads one price list file (xlsx, xls, csv, zip or pdf) for a vendor portal workflow and Vendor Account, optionally attached to one of the workflow's runs, and returns it as listed.",
    }),
} as const;

export type VendorPortalFile = z.infer<typeof VendorPortalFileSchema>;
export type VendorPortalFileSource = z.infer<
  typeof VendorPortalFileSourceSchema
>;
export type VendorPortalUploadTarget = z.infer<
  typeof VendorPortalUploadTargetSchema
>;
export type VendorPortalVendor = z.infer<typeof VendorPortalVendorSchema>;
export type VendorPortalFileType = z.infer<typeof VendorPortalFileTypeSchema>;
export type VendorPortalArchiveFileRange = z.infer<
  typeof VendorPortalArchiveFileRangeSchema
>;
export type CheckVendorPortalFilesArchiveOutput = z.infer<
  typeof CheckVendorPortalFilesArchiveOutputSchema
>;
export type DeleteVendorPortalFilesOutput = z.infer<
  typeof DeleteVendorPortalFilesOutputSchema
>;
